/*---------------------------------------------------------*\
 * Imports
\*---------------------------------------------------------*/
import keyboard from 'keycode-ui';

/*---------------------------------------------------------*\
 * Globals
\*---------------------------------------------------------*/
const cancelEvent = (event) => {
  event.preventDefault();
  event.stopImmediatePropagation();
};

/*---------------------------------------------------------*\
 * The News12List class is designed to manage the navigation of a pageable sequence of items,
 * like the video list.
 *
 * It displays all its elements using the same button classes in a vertical layout. It manages
 * proper tabbing and arrow key navigation.
\*---------------------------------------------------------*/
class News12List {
  //-----------------------------------
  // Constructor
  //-----------------------------------
  constructor($timeout) {
    this.name = 'list';

    this.$timeout = $timeout;

    this._selectedIndex = 0;
    this._labels = [];
    this._datas = [];
    this._item = 7;
    this._startIndex = 0;
    this.listId = this.listId || 'NO_LIST_ID_PROVIDED';

    this.itemsPerColumn = this.itemsPerColumn || 7;
    this.itemsPerScroll = 1;

    this.showIcon = this.showIcon === undefined ? true : this.showIcon;
    this.showArrows = this.showArrows === undefined ? true : this.showArrows;

    this._upIsPrev = this._upIsPrev === undefined ? true : this._upIsPrev;
    this._downIsNext = this._downIsNext === undefined ? true : this._downIsNext;

    this.cancelAllDownKeyEvents = this.cancelAllDownKeyEvents === undefined ? false : this.cancelAllDownKeyEvents;
    this.cancelAllUpKeyEvents = this.cancelAllUpKeyEvents === undefined ? false : this.cancelAllUpKeyEvents;

    this.ignoreNextUp = false;

    this.labelField = this.labelField || 'label';
  }

  //-----------------------------------
  // Properties
  //-----------------------------------
  set upIsPrev(value) {
    this._upIsPrev = (value !== 'false' && value !== false);
  }

  get upIsPrev() {
    return this._upIsPrev;
  }

  set downIsNext(value) {
    this._downIsNext = (value !== 'false' && value !== false);
  }

  get downIsNext() {
    return this._downIsNext;
  }

  set items(value) {
    if (this._items === value) {
      return;
    }

    this._items = value;
    this._selectedIndex = this._startIndex = 0;

    this._labels = [];
    this._datas = [];

    if (this._items && this._items.length) {
      this._items.forEach((item) => {
        this._labels.push(item.label);
        this._datas.push(item.data);
      });
    }

    if (this.focusOnPopulateIx !== undefined) {
      const ix = this.focusOnPopulateIx;

      this.$timeout(() => this.focus(ix));

      this.focusOnPopulateIx = undefined;
    }

    if (this.changeHandler) {
      this.changeHandler({
        list: this
      });
    }
  }

  // -----------------------------------
  // items
  // -----------------------------------
  get items() {
    return this._items;
  }

  // -----------------------------------
  // itemsPerColumn
  // -----------------------------------
  set itemsPerColumn(value) {
    this._itemsPerColumn = typeof value === 'string' ? parseInt(value, 10) : value;
  }

  get itemsPerColumn() {
    return this._itemsPerColumn;
  }

  // -----------------------------------
  // itemsPerScroll
  // -----------------------------------
  set itemsPerScroll(value) {
    this._itemsPerScroll = typeof value === 'string' ? parseInt(value, 10) : value;
  }

  get itemsPerScroll() {
    return this._itemsPerScroll;
  }

  // -----------------------------------
  // displayItems
  // -----------------------------------
  /**
   * Returns only the items that have been determined to be displayable based on the display
   * startIndex and the itemsPerColumn.
   *
   * @returns {*}
   */
  get displayItems() {
    return (this._items && this._items.length) ?
      this._items.slice(this._startIndex, this._startIndex + this.itemsPerColumn) :
      [];
  }

  // -----------------------------------
  // labels
  // -----------------------------------
  get labels() {
    return this._labels;
  }

  // -----------------------------------
  // datas
  // -----------------------------------
  get datas() {
    return this._datas;
  }

  // -----------------------------------
  // item
  // -----------------------------------
  set item(value) {
    this._item = value;
  }

  get item() {
    return this._item;
  }

  // -----------------------------------
  // startIndex
  // -----------------------------------
  set startIndex(value) {
    this._startIndex = value;
  }

  get startIndex() {
    return this._startIndex;
  }

  // -----------------------------------
  // selectedIndex
  // -----------------------------------
  get selectedIndex() {
    return this._selectedIndex;
  }

  set selectedIndex(index) {
    if (!this.items || this.items.length === 0) {
      this._selectedIndex = 0;
      return;
    }

    this._selectedIndex = index;

    if (this._selectedIndex < 0) {
      this._selectedIndex = 0;
    } else if (this._selectedIndex > this.items.length - 1) {
      this._selectedIndex = this.items.length - 1;
    }

    if (this.startIndex > this._selectedIndex || this.startIndex + (this.itemsPerColumn - 1) < this._selectedIndex) {
      this.startIndex = this._selectedIndex - (this._selectedIndex % this.itemsPerColumn);
    }

    this.focusSelectedIndex();

    if (this.changeHandler) {
      this.changeHandler({
        list: this
      });
    }
  }

  // -----------------------------------
  // selectedItem
  // -----------------------------------
  get selectedItem() {
    return this.items[this._selectedIndex];
  }

  // -----------------------------------
  // showPrev
  // -----------------------------------
  get showPrev() {
    return this.startIndex > 0;
  }

  // -----------------------------------
  // showNext
  // -----------------------------------
  get showNext() {
    if (!this.items) {
      return false;
    }

    return this.startIndex + this.itemsPerColumn < this.items.length;
  }

  // -----------------------------------
  // curPage
  // -----------------------------------
  get curPage() {
    return Math.floor(this.selectedIndex / this.itemsPerColumn) + 1;
  }

  // -----------------------------------
  // totalPages
  // -----------------------------------
  get totalPages() {
    if (this.items && this.items.length) {
      return Math.ceil(this.items.length / this.itemsPerColumn);
    }

    return 0;
  }

  //-----------------------------------
  // Methods
  //-----------------------------------
  focus(index, focusOnPopulate, zeroIndex) {
    index = index || 0;

    if (focusOnPopulate) {
      this.focusOnPopulateIx = index;
      return;
    }

    this.selectedIndex = (zeroIndex ? 0 : this.startIndex) + index;
  }

  focusSelectedIndex(ignoreTimeout) {
    const elId = this.listId + '_li_' + this._selectedIndex;
    const el = document.getElementById(elId);

    if (el) {
      el.focus();
    } else if (!ignoreTimeout) {
      this.$timeout(this.focusSelectedIndex.bind(this, true));
    }
  }

  pageDown() {
    if (this.items && this.items.length > this.startIndex + this.itemsPerColumn) {
      this.startIndex += this.itemsPerScroll;

      this.$timeout(() => {
        this.selectedIndex += this.itemsPerScroll;
      });
    }
  }

  pageUp() {
    if (this.startIndex > 0) {
      this.startIndex -= this.itemsPerScroll;

      if (this.startIndex < 0) {
        this.startIndex = 0;
      }

      this.$timeout(() => {
        this.selectedIndex -= this.itemsPerScroll;
      });
    }
  }

  //-----------------------------------
  // Events
  //-----------------------------------
  focusHandler(ix) {
    this.selectedIndex = this.startIndex + ix;
  }

  keyPressHandler($index, event) {
    if (keyboard.isEnter(event)) {
      const index = this.startIndex + $index;

      if (this.itemClickHandler) {
        this.itemClickHandler({
          index,
          $index,
          item: this._items[index],
          event
        });
      }
    }
  }

  keyDownHandler($index, event) {
    const cancel = (event) => {
      cancelEvent(event);
      this.ignoreNextUp = true;
    };

    this.canNext = this.canPrev = false;

    // Down arrow or tab key
    if (keyboard.isTab(event) || keyboard.isDown(event)) {
      const nextItem = this.selectedIndex + 1;

      if ($index === this.itemsPerColumn - 1 && this.items.length - 1 > this.startIndex + $index) {
        this.pageDown();
        cancel(event);
      } else if (nextItem <= this.items.length) {
        this.selectedIndex = nextItem === this.items.length ? this.selectedIndex : nextItem;
        if (nextItem < this.items.length) {
          cancel(event);
        }
      }
    } else if (keyboard.isShiftTab(event) || keyboard.isUp(event)) {
      // Up arrow or shift tab
      if ($index % this.itemsPerColumn === 0 && this.startIndex > 0) {
        this.pageUp();
        cancel(event);
      } else if (this.selectedIndex > 0) {
        this.selectedIndex--;
        cancel(event);
      }
    }

    if (((keyboard.isTab(event) || keyboard.isDown(event)) && (this.startIndex + $index === this.items.length - 1)) ||
      keyboard.isRight(event)) {
      if (this.nextComponentHandler) {
        this.canNext = true;
        cancelEvent(event);
      }
    } else if ((keyboard.isUp(event) && this.selectedIndex === 0) || keyboard.isLeft(event)) {
      if (this.prevComponentHandler) {
        this.canPrev = true;
        cancelEvent(event);
      }
    }

    if ((this.cancelAllDownKeyEvents && keyboard.isDown(event)) ||
      (this.cancelAllUpKeyEvents && keyboard.isUp(event))) {
      cancelEvent(event);
    }
  }

  keyUpHandler($index, event) {
    if (this.ignoreNextUp) {
      cancelEvent(event);
      this.ignoreNextUp = false;
      return;
    }

    let nextIndex;

    const down = keyboard.isDown(event) && this.selectedIndex === this.items.length - 1;
    const right = keyboard.isRight(event);
    const up = keyboard.isUp(event) && this.selectedIndex === 0;
    const left = keyboard.isLeft(event);

    if (this.canNext && ((down && this.downIsNext) || right)) {
      if (this.nextComponentHandler) {
        nextIndex = keyboard.isDown(event) ? 0 : this.selectedIndex - this.startIndex;

        this.nextComponentHandler(nextIndex, keyboard.isDown(event), this.selectedItem);
      }
    } else if (this.canPrev && ((up && this.upIsPrev) || left)) {
      if (this.prevComponentHandler) {
        nextIndex = keyboard.isUp(event) ? this.itemsPerColumn : this.selectedIndex - this.startIndex;

        this.prevComponentHandler(nextIndex, keyboard.isUp(event), this.selectedItem);
      }
    }

    if ((this.canNext && (down || right)) ||
      (this.canPrev && (up || left))) {
      cancelEvent(event);
    }

    if ((this.cancelAllDownKeyEvents && keyboard.isDown(event)) ||
      (this.cancelAllUpKeyEvents && keyboard.isUp(event))) {
      cancelEvent(event);
    }
  }
}

News12List.$inject = [
  '$timeout'
];

export default News12List;



/** WEBPACK FOOTER **
 ** ./client/app/components/common/news12List/news12List.controller.js
 **/